<template>
	<div>
		<div class="d-flex flex-wrap flex-sm-row flex-column-reverse justify-space-between align-sm-center">
			<!-- breadcrumbs -->
			<v-breadcrumbs
				:items="[
					{
						type: 'classId',
						disabled: false,
					},
					{
						type: 'courseId',
						disabled: false,
					},
					{
						type: 'text',
						disabled: false,
						text: 'العلامات'
					},
				]"
				large
				class="pa-0 breadcrumbs mt-sm-0 mt-3"
			>
				<template v-slot:divider>
					<v-icon size="18">mdi-chevron-left</v-icon>
				</template>
				<template v-slot:item="{ item }">
					<v-breadcrumbs-item
						v-if="item.type === 'text'"
						exact
						active-class="font-weight-bold black--text"
						:to="item.to"
						:disabled="item.disabled"
					>
						<v-icon v-if="item.icon" size="20" class="me-2">{{item.icon}}</v-icon>
						<v-img v-else-if="item.img" :src="item.img" width="20" height="20" class="me-1"/>
						{{ item.text }}
					</v-breadcrumbs-item>

					<v-autocomplete
						v-else-if="item.type === 'classId'"
						v-model="classId"
						:items="classes"
						label="الصف"
						item-text="name"
						item-value="id"
						:disabled="loading || loadingMarks || printingLoading"
						hide-details
						dense
						outlined
						style="max-width: 160px"
						@change="fetchClassData()"
					></v-autocomplete>
					
					<v-autocomplete
						v-else-if="item.type === 'courseId'"
						v-model="courseId"
						:items="courses"
						item-text="title"
						item-value="id"
						:loading="loadingClassData"
						:disabled="loading || loadingMarks || printingLoading"
						dense
						hide-details
						outlined
						label="الشعبة"
						:no-data-text="classId ? 'لا يوجد شعب ضمن هذا الصف' : 'قم باختيار صف أولا '"
						style="max-width: 160px"
						@change="updateRouteForCourseId"
					></v-autocomplete>
				</template>
			</v-breadcrumbs>

			<div>
				<!--print-->
				<v-btn
					outlined
					height="30"
					class="me-3"
					:disabled="!courseId || !conceptId"
					:loading="printingLoading"
					@click="generateConceptReport"
				>
					<v-icon class="me-2">$printer</v-icon>
					تقرير concept للطلاب
				</v-btn>

				<!--marks scales -->
				<v-btn
					v-if="typeof classId === 'number' && typeof courseId === 'number'"
					outlined
					height="30"
					class="me-3"
					:to="{ name: 'subjects-scales', params: { classId, courseId }, query: $route.query }"
				>
					<v-icon class="me-2">mdi-tag-check</v-icon>
					مقاييس العلامات
				</v-btn>

				<!--print-->
				<v-btn v-if="false" outlined height="30" class="me-3" disabled>
					<v-icon class="me-2">$printer</v-icon>
					تقرير الطلاب المتفوقين
				</v-btn>
				
				<!-- export -->
				<v-btn v-if="false" outlined height="30" disabled>
					<v-icon class="me-2">$excel</v-icon>
					تصدير لملف Excel
				</v-btn>
			</div>
		</div>

		<v-divider class="my-3"/>

		<!-- show -->
		<div class="d-flex flex-wrap justify-space-between align-center mb-2">
			<!-- show for -->
			<div class="d-flex flex-wrap align-center" style="max-width: 100%;">
				<template v-if="!loadingClassData && concepts.length">
					<span class="me-1 text-no-wrap">عرض بيانات: </span>
					<v-chip-group
						v-model="conceptId"
						color="primary"
						class="d-flex align-center"
						show-arrows
						center-active
						@change="$router.replace({ query: { ...$route.query, conceptId } })"
					>
						<v-chip
							v-for="concept in concepts"
							:key="concept.id"
							small
							:value="concept.id"
							:disabled="loading || loadingMarks || printingLoading"
							class="my-0"
						>
							{{concept.name}}
						</v-chip>
					</v-chip-group>
				</template>
			</div>

			<!-- show as -->
			<div class="d-flex flex-wrap align-center">
				<span class="me-1 text-no-wrap">عرض العلامات كـ: </span>
				<v-radio-group
					v-model="showMarksAs"
					row
					hide-details
					class="mt-0"
					@change="$router.replace({ query: { ...$route.query, showMarksAs } })"
				>
					<v-radio
						label="قيم رقمية"
						:value="gradeViewType.number"
						color="green"
						:ripple="false"
					/>
					<v-radio
						label="نسب مئوية"
						:value="gradeViewType.percentage"
						color="green"
						:ripple="false"
					/>
					<v-radio
						label="ترميز"
						:value="gradeViewType.symbol"
						color="green"
						:ripple="false"
					/>
				</v-radio-group>
			</div>
		</div>

		<!-- loading -->
		<div
			v-if="loading"
			class="fill-height d-flex justify-center align-center"
			:style="`height:${$vuetify.breakpoint.height - 200}px`"
		>
			<v-progress-circular
				color="primary"
				size="40"
				indeterminate
			/>
		</div>

		<!-- table -->
		<bee-handy-table
			v-if="$route.params.courseId !== undefined"
			v-show="!loading"
			ref="table"
			:headers="flattedHeaders"
			:items="[]"
			:loading="loadingMarks"
			dense
			zebra
			fixed-header
			:items-per-page="50"
			:hide-default-header="$vuetify.breakpoint.smAndUp"
			hide-default-footer
			pagination-on-scroll
			pagination-on-scroll-server-items
			:server-items-length="totalItemsLength"
			shown-rows-text
			with-columns-divider
			table-class="overflow-hidden"
			:data-table-props="{ disableSort: true }"
			:height="$vuetify.breakpoint.height - 200"
			@pagination-on-scroll:reach-last-row="fetchMarks"
		>
			<template v-if="$vuetify.breakpoint.smAndUp" v-slot:header>
				<thead class="v-data-table-header">
					<!-- top headers -->
					<tr>
						<th
							v-for="(header, i) in nestedHeaders"
							:key="header.text + i"
							:colspan="header.subHeaders.length || null"
							:class="[
								header.colorClass,
								header.columnClass,
								{ 'v-data-table__divider': header.divider },
								`text-${header.align}`,
								'text-capitalize'
							]"
							:style="{
								width: typeof header.width === 'number' ? `${header.width}px` : header,
								minWidth: typeof header.width === 'number' ? `${header.width}px` : header
							}"
							role="columnheader"
						>{{header.text}}</th>
					</tr>

					<!-- sub headers -->
					<tr>
						<th
							v-for="(header, i) in flattedHeaders"
							:key="header.text + i"
							:class="[
								header.colorClass,
								header.columnClass,
								{ 'v-data-table__divider': header.divider },
								`text-${header.align}`,
								'text-capitalize'
							]"
							role="columnheader"
							:style="{
								width: typeof header.width === 'number' ? `${header.width}px` : header,
								minWidth: typeof header.width === 'number' ? `${header.width}px` : header
							}"
						>
							<div
								class="d-inline-flex pb-1"
								:class="{ 'rotate-header-90': !header.noRotate}"
							>
								{{header.text || '-'}}
							</div>
						</th>
					</tr>
				</thead>
			</template>

			<!-- temp -->
			<template
				v-for="(header, i) in flattedHeaders"
				v-slot:[`item.${header.name}`]="{ item, getShownValueInCell }"
			>
				<span
					v-if="header.name !== 'studentName'"
					:key="i"
					:class="{
						'error--text font-weight-black': item[header.value]?.mark === -1
					}"
				>
					{{
						typeof item[header.value]?.mark === 'number'
							? getShownValueInCell({ item, header })
							: '-'
					}}
				</span>
				<router-link
					v-else
					:to="{
						name: 'student-marks',
						params: {
							classId: classId,
							courseId: courseId,
							studentId: item.id
						},
						query: { conceptId }
					}"
					:key="i"
					class="text-decoration-none"
				>
					{{ item.studentName }}
				</router-link>
			</template>
		</bee-handy-table>

		<v-row v-else justify="center" align="center">
			<v-col
				cols="4"
				class="d-flex flex-column justify-center align-center"
				:style="`height:${$vuetify.breakpoint.height - 200}px`"
			>
				<img src="@/assets/no-data.png" alt="" width="200" class="mb-5">
				<p class="text-h4 font-weight-bold">
					أدخل الصف والشعبة
				</p>
				<p class="text-h6 font-weight-regular text-center">
					ليتم إظهار علامات الطلاب يرجى اختيار الصف والشعبة التي تود عرض علامات طلابه
				</p>
				<div class="d-flex">
					<v-autocomplete 
						v-model="classId"
						label="الصف"
						item-text="name"
						item-value="id"
						hide-details
						dense
						outlined 
						class="rounded-md mx-1"
						:items="classes"
						@change="fetchClassData()"
					></v-autocomplete>
					
					<v-autocomplete 
						v-model="courseId"
						:items="courses"
						item-text="title"
						item-value="id"
						:loading="loadingClassData"
						dense
						hide-details
						outlined 
						class="rounded-md mx-1"
						label="الشعبة"
						:no-data-text="classId ? 'لا يوجد شعب ضمن هذا الصف' : 'قم باختيار صف أولا '"
						@change="updateRouteForCourseId"
					></v-autocomplete>
				</div>
			</v-col>
		</v-row>
	</div>
</template>

<script>
import { gradeViewType } from '@/helpers/enums';
import { mapGetters, mapState } from 'vuex';
import { generateExamReport } from '@/helpers/generate-html';
import printJS from 'print-js';
import { fixNumber, generateResultOptionsData, getLateralGrade } from '@/helpers/functions';
export default {
	name: 'Marks',

	data: () => ({
		gradeViewType,

		showMarksAs: gradeViewType.number,

		loading: false,
		loadingClassData: false,
		loadingMarks: false,
		printingLoading: false,

		classId: null,
		courseId: null,
		conceptId: null,
		
		courses: [],
		concepts: [],
		subjectHeaders: [],

		totalItemsLength: 0,
	}),

	watch: {
		$route: {
			handler (newVal, oldVal) {
				const oldRoute = oldVal || { params: {}, query: {} };

				this.classId = +newVal.params.classId || null;
				this.courseId = +newVal.params.courseId || null;
				this.conceptId = +newVal.query.conceptId || null;
				this.showMarksAs = +newVal.query.showMarksAs || gradeViewType.number;

				if (typeof this.classId === 'number' && this.classId !== +oldRoute.params.courseId) {
					this.fetchClassData();
				}
				if (typeof this.courseId === 'number' && this.courseId !== +oldRoute.params.courseId) {
					this.fetchTableData();
				} else {
					if ((typeof this.conceptId === 'number' && this.conceptId !== +oldRoute.query.conceptId)) {
						this.fetchMarks();
					}
				}
			},
			immediate: true,
			deep: true,
		}
	},

	computed: {
		...mapState({
			classes: state => state.classes.classes,
		}),

		...mapGetters({
			getConceptById: 'classes/getConceptById',
		}),

		nestedHeaders() {
			const headers = [
				{
					text: 'المحور',
					width: 60,
					columnClass: 'px-2',
					subHeaders: [
						{
							text: 'اسم الطالب',
							name: 'studentName',
							value: 'studentName',
							noRotate: true,
							useParentColor: true,
						}
					]
				},
				...this.subjectHeaders
			];

			for (let i = 0; i < headers.length; i++) {
				headers[i].columnClass = headers[i].columnClass ? headers[i].columnClass + ' px-0' : 'px-0';
				headers[i].colorClass = i === 0 ? 'color-1' : `color-${((i - 1) % 6) + 1}`;
				headers[i].align = 'center';
				headers[i].width =
					headers[i].width ? headers[i].width : (this.$vuetify.breakpoint.lgAndUp ? 30 : null);
			}
			return headers;
		},

		flattedHeaders() {
			return this.nestedHeaders.flatMap(header => {
				if (header.subHeaders) {
					return header.subHeaders.map(child => ({
						...child,
						noNotAvailableText: true,
						width: child.width ? child.width : header.width,
						align: 'center',
						colorClass:
							!child.colorClass
								? header.colorClass + (!child.useParentColor ? '-light' : '')
								: child.colorClass,
						cellClass:
							!child.colorClass
								? header.colorClass + (!child.useParentColor ? '-opacity' : '-text')
								: child.colorClass,
						columnClass: header.columnClass + ' ' + (child.columnClass || ''),
						setValueExpr: val => {
							const mark = val.mark || 0;
							const maxMark = val.maxMark || 0;
							return val.mark !== -1
								? (() => {
									const percent = fixNumber((mark / (maxMark || 1)) * 100, 2);
									switch (this.showMarksAs) {
									case gradeViewType.number: return mark;
									case gradeViewType.percentage: return percent + '%';
									case gradeViewType.symbol: return (
										!val.scales.length
											? getLateralGrade(percent)?.code
											: val.scales.find(c => c.orderIndex === mark)?.code ?? getLateralGrade(percent)?.code
									);
									default: return mark
									}
								})()
								: 'غ'
						}
					}))
				}
				return header;
			})
		},

		marks() {
			const marks = [];
			const valuesKeys = [
				'readingInPhrases',
				'pace',
				'syntax',
				'selfCorrections',
				'intonation',
				'letterNamingFluency',
				'slightWordFluency',
				'comprehension',
				'phonemeCategorization',
				'phonemeBlending',
				'phonemeSegmentation',
				'spelling',
				'vocabulary',
				'ideasAndContent',
				'grammar',
				'structuralAnalysis',
				'writing',
				'interactiveReadAloud',
				'retellingCards',
				'participation',
				'completionOfWork',
				'concentration',
				'selfDirection',
				'cooperationWithOthers',
				'behavior',
				'presentationOfAssignments',
				'movementVideo'
			];

			for (let i = 0; i < 100; i++) {
				marks.push(
					valuesKeys.reduce((obj, key) => {
						obj[key] = Math.random() > 0.1 ? Math.round(Math.random() * 100) : -1;
						return obj;
					}, {
						studentName: 'Student ' + (i + 1),
					})

				)
			}

			return marks;
		}
	},

	methods: {
		fetchClassData() {
			this.loadingClassData = true;
			this.$store.dispatch('classes/fetchClassData', {
				classId: this.classId
			}).then(({ courses, concepts }) => {
				this.courses = courses;
				this.concepts = concepts;
			}).finally(() => {
				this.loadingClassData = false;
			})
		},

		fetchSubjects() {
			if (!this.classId) return;
			return this.$store.dispatch('subjects/fetchByClassId', { classId: this.classId }).then(data => {
				this.subjectHeaders = data.map(c => ({
					text: c.name,
					subHeaders: c.subjectChilds.map(e => ({
						text: e.name,
						name: `subject_${e.id}`,
						value: `subject_${e.id}`,
					}))
				}));
			})
		},

		fetchMarks(newOptions, isUpdate = false) {
			if (!this.courseId) return;
			
			this.loadingMarks = true;

			let page = 1;
			if (newOptions) {
				page = newOptions.page || 1;
			} else if (this.$refs.table) {
				this.$refs.table.resetShownRows();
			}
			return this.$store.dispatch('marks/fetchMarks', { 
				courseId: this.courseId,
				conceptId: this.conceptId,
				page: page,
				perPage: 50,
			}).then(({ data, total }) => {
				this.$refs.table.addScrollItems(
					data.map(c => ({
						id: c.student.id,
						studentName: c.student.username,
						...c.examTest.flatMap(examTest => 
							(examTest.examGrades || []).reduce((res, a) => {
								res[`subject_${a.topic.id}`] = {
									mark: a.grade ? (!a.grade.isAbsent ? a.grade.mark : -1) : null,
									maxMark: a.topicMaxMark,
									scales: a.topic.scales || [],
								};
								return res;
							}, {})
						).reduce((res, a) => ({ ...res, ...a }), {})
					}))
				);

				this.totalItemsLength = total;
			}).finally(() => {
				if (!isUpdate) this.loadingMarks = false;
			})
		},

		fetchTableData() {
			if (this.courseId) {
				this.loading = true;
				Promise.all([
					this.fetchSubjects(),
					this.fetchMarks(null, true)
				]).finally(() => {
					this.loadingMarks = false;
					this.loading = false;
				})
			}
		},

		updateRouteForCourseId() {
			if (this.courseId && this.$route.params.courseId !== this.courseId) {
				this.$router.replace({
					name: 'marks',
					params: { classId: this.classId, courseId: this.courseId },
					query: { conceptId: this.concepts[0]?.id }
				})
			}
		},

		generateConceptReport() {
			this.printingLoading = true;
			this.$store.dispatch('marks/fetchStudentsMarks', {
				courseId: this.courseId,
				conceptId: this.conceptId,
			}).then(data => {
				if (!data.length) {
					this.$snackbar.alert('لا يوجد طلاب بعلامات جميعها مدخل');
					return;
				}

				// ordering by subjects
				this.data = data.map(c => {
					// create a lookup map from text to index
					const orderedMap = new Map(this.flattedHeaders.map((item, index) => [item.text, index]));
	
					return {
						student: c.student,
						// sort array a using the text order
						grade: c.grade.sort((a, b) =>
							orderedMap.get(a.topic.name) - orderedMap.get(b.topic.name)
						)
					}
				});

				const printData = data.map(obj => {
					const groupedMarks = [];
					for (const grade of obj.grade) {
						const index = groupedMarks.findIndex(c => c.subject === grade.topic.parent.name);
						
						const lateralGradeNum =
							getLateralGrade(fixNumber(grade.mark / (grade.maxMark || 1) * 100, 2) || 0)?.num;
						
						const childData = {
							attendance: !grade.isAbsent,
							numeralMark: grade.mark,
							mark:
								!grade.isAbsent
									? !grade.topic.scales.length
										? lateralGradeNum
										: grade.topic.scales.find(t => t.orderIndex === grade.mark)?.orderIndex ??
											lateralGradeNum
									: -1,
							scales: grade.topic.scales.sort((a, b) => a.orderIndex - b.orderIndex).map(c => c.code),
							maxMark: grade.maxMark,
							includeInResults: grade.topic.includeInExamTestResult,
							isManualCorrect: grade.topic.isManuallyCorrected,
						}

						if (index === -1) {
							groupedMarks.push({
								subject: grade.topic.parent.name,
								children: [{
									subject: grade.topic.name,
									...childData
								}]
							})
						} else {
							groupedMarks[index].children.push({
								subject: grade.topic.name,
								...childData
							})
						};
					}
					return {
						data: groupedMarks,
						options: {
							headerText: `
								Name: ${obj.student.username}
								${'&nbsp;'.repeat(2)}
								Class: ${obj.course.specialty.name}
								${'&nbsp;'.repeat(2)}
								Section: ${obj.course.title}
								${'&nbsp;'.repeat(1)}
								School Year: 2025-2026
								${'&nbsp;'.repeat(10)}
								${this.getConceptById(this.conceptId)?.name} / ${this.concepts.length}
							`,
							...generateResultOptionsData(groupedMarks)
						}
					}
				});
				
				const html = generateExamReport(printData, { multiple: true });
				
				printJS({ printable: html, type: 'raw-html' });
			}).finally(() => {
				this.printingLoading = false;
			})
		}
	},
}
</script>

<style>

</style>